import { Checkbox, Space, Table, Typography } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import type { CSSProperties, Key } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppScope } from '@/types'
import RelatedApisPopover from './RelatedApisPopover'
import ScopeDetailPopover from './ScopeDetailPopover'

type Props = {
  data: AppScope[]
  categoryNames: Record<string, string>
  loading?: boolean
  selectedRowKeys: Key[]
  onToggleRow: (scope: AppScope, selected: boolean) => void
  onToggleAll: (selected: boolean) => void
  allSelected: boolean
  someSelected: boolean
  selectAllDisabled?: boolean
  selectingAll?: boolean
  onClose: (scope: AppScope) => void
  onWithdraw: (scope: AppScope) => void
  onDelete: (scope: AppScope) => void
}

const actionPrimary: CSSProperties = {
  color: '#6366F1',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}
const actionDanger: CSSProperties = {
  color: '#FF383C',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}

export default function ScopeTable({
  data,
  categoryNames,
  loading,
  selectedRowKeys,
  onToggleRow,
  onToggleAll,
  allSelected,
  someSelected,
  selectAllDisabled,
  selectingAll,
  onClose,
  onWithdraw,
  onDelete,
}: Props) {
  const { t } = useTranslation()

  const headerNowrap = { style: { whiteSpace: 'nowrap' as const } }

  const columns: ColumnsType<AppScope> = [
    {
      title: t('scope.name'),
      dataIndex: 'name',
      onHeaderCell: () => ({ style: { paddingLeft: 0 } }),
      onCell: () => ({ style: { paddingLeft: 0 } }),
      render: (name: string, record) => (
        <ScopeDetailPopover scope={record}>
          <div
            style={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'default',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>{name}</div>
              <div
                style={{
                  borderTop: '1px dashed rgba(0, 0, 0, 0.35)',
                  marginTop: 3,
                }}
              />
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 13,
                lineHeight: '20px',
                color: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              {record.scope_id}
            </div>
          </div>
        </ScopeDetailPopover>
      ),
    },
    {
      title: t('scope.category'),
      dataIndex: 'category',
      width: 100,
      onHeaderCell: () => headerNowrap,
      render: (category: string) => (
        <span style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>
          {categoryNames[category] || category}
        </span>
      ),
    },
    {
      title: t('scope.identityType'),
      dataIndex: 'identity_type',
      width: 140,
      onHeaderCell: () => headerNowrap,
      render: (identity: AppScope['identity_type']) => (
        <span style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>
          {t(`scope.identity_${identity}`)}
        </span>
      ),
    },
    {
      title: t('scope.sensitivity'),
      dataIndex: 'sensitivity_level',
      width: 100,
      onHeaderCell: () => headerNowrap,
      render: (level: AppScope['sensitivity_level']) => (
        <span style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>
          {t(`scope.level_${level}`)}
        </span>
      ),
    },
    {
      title: <span style={{ paddingLeft: 20 }}>{t('scope.actions')}</span>,
      key: 'actions',
      width: 280,
      fixed: 'right',
      onHeaderCell: () => headerNowrap,
      onCell: () => ({ style: { whiteSpace: 'nowrap', paddingRight: 16 } }),
      render: (_, record) => (
        <Space size={12} wrap={false}>
          <RelatedApisPopover apis={record.related_apis} docUrl={record.doc_url} />
          {record.status === 'approved' && (
            <Typography.Link
              className="scope-action-danger"
              style={actionDanger}
              onClick={() => onClose(record)}
            >
              {t('scope.close')}
            </Typography.Link>
          )}
          {record.status === 'pending' && (
            <Typography.Link style={actionPrimary} onClick={() => onWithdraw(record)}>
              {t('scope.withdraw')}
            </Typography.Link>
          )}
          {record.status === 'rejected' && (
            <Typography.Link style={actionDanger} onClick={() => onDelete(record)}>
              {t('scope.delete')}
            </Typography.Link>
          )}
        </Space>
      ),
    },
  ]

  const rowSelection: TableProps<AppScope>['rowSelection'] = {
    columnWidth: 48,
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    columnTitle: (
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        disabled={selectAllDisabled}
        onChange={(e) => onToggleAll(e.target.checked)}
      />
    ),
    onSelect: (record, selected) => {
      if (record.status !== 'approved') return
      onToggleRow(record, selected)
    },
    getCheckboxProps: (record) => ({
      disabled: selectingAll || record.status !== 'approved',
    }),
  }

  return (
    <>
    <style>{`
      .scope-table .ant-table-content > table,
      .scope-table .ant-table-header > table,
      .scope-table .ant-table-body > table {
        table-layout: fixed !important;
      }
      .scope-table col.ant-table-selection-col,
      .scope-table .ant-table-selection-column {
        width: 48px !important;
        min-width: 48px !important;
        max-width: 48px !important;
        box-sizing: border-box !important;
      }
      .scope-table .ant-table-selection-column {
        padding-left: 20px !important;
        padding-right: 12px !important;
        text-align: left !important;
      }
      .scope-table .ant-table-selection-column .ant-checkbox-wrapper {
        margin-inline-end: 0;
      }
      .scope-table .ant-table-thead > tr > th {
        font-size: 13px !important;
        font-weight: 400 !important;
        line-height: 20px;
        background-color: #f5f5f5 !important;
      }
      /* 滚动条占位格默认带左右 16px 内边距，会宽过滚动条并压住右侧固定列，半透明底叠成深色块 */
      .scope-table .ant-table-thead > tr > th.ant-table-cell-scrollbar {
        padding: 0 !important;
        background-color: #f5f5f5 !important;
        box-shadow: none !important;
      }
      .scope-table .ant-table-cell-scrollbar::after {
        display: none !important;
      }
      .scope-table,
      .scope-table .ant-spin,
      .scope-table .ant-spin-nested-loading,
      .scope-table .ant-spin-container,
      .scope-table .ant-table,
      .scope-table .ant-table-container {
        height: 100%;
        min-height: 0;
      }
      .scope-table {
        overflow: hidden;
      }
      .scope-table .ant-table-container {
        display: flex;
        flex-direction: column;
      }
      .scope-table .ant-table-header {
        flex: none;
        overflow: hidden !important;
      }
      /* scroll.y 只用来拆开表头；表体高度跟容器走，避免改高度时滚动位置被重置 */
      .scope-table .ant-table-body {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none !important;
        overflow-y: auto !important;
        overscroll-behavior: contain;
      }
      .scope-table .scope-action-danger {
        display: inline-flex;
        align-items: center;
        height: 24px;
        padding: 0 8px;
        border-radius: 4px;
      }
      .scope-table .scope-action-danger:hover {
        color: #ff383c !important;
        background: rgba(255, 56, 60, 0.08);
      }
    `}</style>
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={false}
      rowSelection={rowSelection}
      scroll={{ x: 'max-content', y: 1 }}
      className="scope-table h-full [&_.ant-table-thead>tr>th]:!h-11 [&_.ant-table-thead>tr>th]:!py-0 [&_.ant-table-tbody>tr:not(.ant-table-measure-row)>td]:!py-3"
      locale={{ emptyText: t('scope.empty') }}
    />
    </>
  )
}
